import axios from 'axios';
import { API_BASE_URL } from '../utils/config';
import {
  CoachPayment,
  PayableEvents,
  CreateCoachPayment,
  ConfirmPayment
} from '../types/coachPayment';

/**
 * Service for managing coach payment tracking
 */
class CoachPaymentService {
  private getAuthHeader() {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Get payable events for a coach in a given period (Admin only)
   */
  async getPayableEvents(
    coachId: string,
    startDate: Date,
    endDate: Date
  ): Promise<PayableEvents> {
    const queryParams = new URLSearchParams();
    queryParams.append('coachId', coachId);
    queryParams.append('startDate', startDate.toISOString());
    queryParams.append('endDate', endDate.toISOString());

    const response = await axios.get<PayableEvents>(
      `${API_BASE_URL}/api/coach-payment/payable-events?${queryParams}`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  /**
   * Get all payments for a specific coach (Admin only)
   */
  async getCoachPayments(coachId: string): Promise<CoachPayment[]> {
    const response = await axios.get<CoachPayment[]>(
      `${API_BASE_URL}/api/coach-payment/coach/${coachId}`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  /**
   * Get my payments (Coach only)
   */
  async getMyPayments(): Promise<CoachPayment[]> {
    const response = await axios.get<CoachPayment[]>(
      `${API_BASE_URL}/api/coach-payment/my-payments`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  /**
   * Create a new payment (Admin only)
   */
  async createPayment(data: CreateCoachPayment): Promise<CoachPayment> {
    const response = await axios.post<CoachPayment>(
      `${API_BASE_URL}/api/coach-payment`,
      {
        coachId: data.coachId,
        periodStart: data.periodStart.toISOString(),
        periodEnd: data.periodEnd.toISOString(),
        eventIds: data.eventIds,
        customItems: data.customItems.map(item => ({
          category: item.category,
          description: item.description,
          date: item.date instanceof Date ? item.date.toISOString() : item.date,
          hours: item.hours,
          amount: item.amount
        })),
        adminNotes: data.adminNotes
      },
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  /**
   * Mark payment as paid (Admin only)
   */
  async markAsPaid(paymentId: string): Promise<CoachPayment> {
    const response = await axios.put<CoachPayment>(
      `${API_BASE_URL}/api/coach-payment/${paymentId}/mark-paid`,
      {},
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  /**
   * Confirm payment receipt (Coach only)
   */
  async confirmPayment(paymentId: string, data?: ConfirmPayment): Promise<CoachPayment> {
    const response = await axios.put<CoachPayment>(
      `${API_BASE_URL}/api/coach-payment/${paymentId}/confirm`,
      data || {},
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  /**
   * Delete payment (Admin only, only if PendingAdminReview)
   */
  async deletePayment(paymentId: string): Promise<void> {
    await axios.delete(
      `${API_BASE_URL}/api/coach-payment/${paymentId}`,
      { headers: this.getAuthHeader() }
    );
  }
}

const coachPaymentService = new CoachPaymentService();
export default coachPaymentService;
